import { FC, useEffect, useState } from 'react';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

export interface NotificationProps {
  message: string;
  type: NotificationType;
  duration?: number;
  onClose?: () => void;
}

interface NotificationState extends NotificationProps {
  id: string;
  isVisible: boolean;
}

// Custom hook for partner notifications
let setNotifications: React.Dispatch<React.SetStateAction<NotificationState[]>> | null = null;

export const usePartnerNotification = (): { showNotification: (notification: NotificationProps) => void } => {
  const showNotification = (notification: NotificationProps): void => {
    const id = Date.now().toString();
    const newNotification: NotificationState = {
      ...notification,
      id,
      isVisible: true,
      duration: notification.duration || 4000,
    };

    if (setNotifications) {
      setNotifications((prev) => [...prev, newNotification]);

      // Auto-hide after duration
      setTimeout(() => {
        if (setNotifications) {
          setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isVisible: false } : n)));

          // Remove after animation
          setTimeout(() => {
            if (setNotifications) {
              setNotifications((prev) => prev.filter((n) => n.id !== id));
            }
          }, 300);
        }
      }, newNotification.duration);
    }
  };

  return { showNotification };
};

const PartnerNotification: FC<NotificationState> = ({ message, type, isVisible, onClose }) => {
  const getNotificationStyles = (): string => {
    const baseStyles = `
      fixed top-6 left-1/2 transform -translate-x-1/2 z-[9999]
      min-w-[320px] max-w-[500px] mx-4
      bg-[#09101e] border-2 rounded-xl p-4
      font-poppins text-white shadow-2xl
      transition-all duration-300 ease-in-out
      ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}
    `;

    switch (type) {
      case 'success':
        return `${baseStyles} border-[rgba(197,237,0,1)] shadow-[0_0_20px_rgba(197,237,0,0.3)]`;
      case 'error':
        return `${baseStyles} border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.3)]`;
      case 'warning':
        return `${baseStyles} border-yellow-500 shadow-[0_0_20px_rgba(234,179,8,0.3)]`;
      case 'info':
      default:
        return `${baseStyles} border-blue-500 shadow-[0_0_20px_rgba(59,130,246,0.3)]`;
    }
  };

  const getIconColor = (): string => {
    switch (type) {
      case 'success':
        return 'text-[rgba(197,237,0,1)]';
      case 'error':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-500';
      case 'info':
      default:
        return 'text-blue-500';
    }
  };

  const getIcon = (): JSX.Element => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        );
    }
  };

  return (
    <div className={getNotificationStyles()}>
      <div className="flex items-center space-x-3">
        <div className={`flex-shrink-0 ${getIconColor()}`}>{getIcon()}</div>
        <div className="flex-1">
          <p className="text-sm font-medium leading-5">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 text-gray-400 hover:text-white transition-colors duration-200"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Container component for managing notifications
export const PartnerNotificationContainer: FC = () => {
  const [notifications, setNotificationsState] = useState<NotificationState[]>([]);

  useEffect(() => {
    setNotifications = setNotificationsState;
    return () => {
      setNotifications = null;
    };
  }, []);

  const handleClose = (id: string): void => {
    setNotificationsState((prev) => prev.map((n) => (n.id === id ? { ...n, isVisible: false } : n)));

    setTimeout(() => {
      setNotificationsState((prev) => prev.filter((n) => n.id !== id));
    }, 300);
  };

  return (
    <>
      {notifications.map((notification) => (
        <PartnerNotification
          key={notification.id}
          {...notification}
          onClose={(): void => handleClose(notification.id)}
        />
      ))}
    </>
  );
};

export default PartnerNotification;
